import React, { useRef } from 'react';
import { classNames } from '../utils/classNames';

// A row of joined buttons, one for each whole number from `min` to `max`; the user clicks one value.
// The selected value is blue. When `offValue` is selected (for example 0 = "not used"), it is slate,
// so an "off" value does not look like a normal choice. The caller owns the state: onChange receives
// the next value. Keyboard: Tab enters the group on the selected value; the arrow keys, Home and End
// move and select (the radio group pattern). The caller writes any text beside the buttons.
// Each button is 40 px wide and the row does not wrap: keep a scale to 8 values or fewer for a 400 px screen.
type Props = {
	// The accessible name of the group ("Importance of Hotel A").
	label: string,
	value: number,
	onChange: (value: number) => void,
	min?: number,
	max?: number,
	offValue?: number,
	// The accessible name of each value, from `min` up ("Not used", "Very low", …). Default: the number.
	optionLabels?: string[],
	disabled?: boolean,
};

const ScaleButtons: React.FunctionComponent<Props> = ({
	label,
	value,
	onChange,
	min = 0,
	max = 5,
	offValue,
	optionLabels,
	disabled = false,
}) => {
	const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
	const buttons = useRef<(HTMLButtonElement | null)[]>([]);
	const focusable = values.includes(value) ? value : min;

	const select = (next: number) => {
		const clamped = Math.min(Math.max(next, min), max);
		buttons.current[clamped - min]?.focus();
		if (clamped !== value) onChange(clamped);
	};

	const onKeyDown = (event: React.KeyboardEvent) => {
		const next = value === max ? min : value + 1;
		const previous = value === min ? max : value - 1;
		const keys: Record<string, number> = {
			ArrowRight: next, ArrowDown: next, ArrowLeft: previous, ArrowUp: previous, Home: min, End: max,
		};
		if (!(event.key in keys)) return;
		event.preventDefault();
		select(keys[event.key]);
	};

	return <div role="radiogroup" aria-label={label} aria-disabled={disabled} onKeyDown={disabled ? undefined : onKeyDown}
		className="inline-flex flex-none rounded-md shadow-sm">
		{values.map((v, i) => {
			const selected = v === value;
			const isOff = offValue !== undefined && v === offValue;
			return <button key={v} type="button" role="radio" aria-checked={selected}
				aria-label={optionLabels?.[i] !== undefined ? `${v} (${optionLabels[i]})` : undefined}
				ref={element => { buttons.current[i] = element; }}
				tabIndex={v === focusable ? 0 : -1}
				disabled={disabled}
				onClick={() => { if (!selected) onChange(v); }}
				className={classNames(
					'relative -ml-px h-9 w-10 border text-sm font-medium first:ml-0 first:rounded-l-md last:rounded-r-md',
					'focus:z-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1',
					selected && isOff ? 'z-10 border-slate-500 bg-slate-500 text-white' : '',
					selected && !isOff ? 'z-10 border-blue-600 bg-blue-600 text-white' : '',
					selected ? '' : 'border-slate-300 bg-white text-slate-700',
					disabled ? 'cursor-not-allowed opacity-50' : (selected ? '' : 'hover:bg-slate-50'),
				)}>
				{v}
			</button>;
		})}
	</div>;
};

export default ScaleButtons;
